/* Libraries */
import React from 'react';
import { css } from 'aphrodite';
import { Edit3 } from 'react-feather';
import { useObserver } from 'mobx-react';

/* Regions */
import { getRegionalConfig } from '~/src/regions/config/config';

/* Components */
import Button from '~/src/components/Button';
import Dropdown from '~/src/components/Dropdown';
import Triangle from '~/src/components/Triangle';
import Checkbox from '~/src/components/Checkbox';

/* Styles */
import dropdownStyles from '~/src/components/Dropdown/styles';

import useMst from '~/src/hooks/useMst';
import { useDdhmEditEnabled } from '~/src/entities/user';
import { useLayoutContext } from '~/src/contexts/Layout';
import { LAYOUT_MODAL_TYPES } from '~/src/components/PageLayout/Modals';
import { isClioExportModalDismissed } from '~/src/components/Modal/clioExportModalUtils';
import { MODAL_VARIANTS } from '~/src/components/Modal/ClioManageExportModal';
import styles from '../../components/Footers/styles';

export type DraftingFooterProps = {
  onCurrentDownload?: () => void;
  onEdit?: () => void;
  onCreateEsign: () => void;
  onSaveInClio: (
    openInNewTab?: boolean,
    selectedFolderId?: number,
  ) => Promise<void>;
  onAllDownload: () => void;
  disableSaveInClio?: boolean;
  disableEdit?: boolean;
  disableEsign?: boolean;
  disableDownload?: boolean;
  showHighlights?: boolean;
  onToggleShowHighlights?: () => void;
  isHighlightingEnabled?: boolean;
  newVersionAvailable?: boolean;
};

export const DraftingFooter = ({
  onEdit,
  onCurrentDownload,
  onAllDownload,
  onCreateEsign,
  onSaveInClio,
  disableSaveInClio,
  disableEdit,
  disableEsign,
  disableDownload,
  showHighlights,
  onToggleShowHighlights,
  isHighlightingEnabled,
  newVersionAvailable,
}: DraftingFooterProps) => {
  let toggleDropdownCallback: () => void;
  const isDdhmEditEnabled = useDdhmEditEnabled();
  const { showModal } = useLayoutContext();
  const { isEsignEnabled } = getRegionalConfig();

  const {
    totalNumber,
    clioStore,
    isProjectsFetching,
    isFolderDeleted,
    matterId,
    parentId,
  } = useMst((store) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useObserver(() => ({
      totalNumber: store.sidebarItems.items.length,
      clioStore: store.clio,
      isProjectsFetching: store.projects.isLoading,
      // Direct access to store values - no useState or useEffect needed!
      isFolderDeleted: store.clio.courtFormIsClioFolderDeleted || false,
      matterId: store.clio.courtFormClioMatterId || null,
      parentId: store.clio.courtFormClioParentId || null,
    })),
  );

  const handleCurrentDownload = () => {
    onCurrentDownload?.();
    toggleDropdownCallback();
  };

  const handleAlDownload = () => {
    onAllDownload?.();
    toggleDropdownCallback();
  };

  const handleSaveInClio = () => {
    if (isDdhmEditEnabled && parentId && !isFolderDeleted) {
      if (!newVersionAvailable && isClioExportModalDismissed()) {
        onSaveInClio();
        return;
      }

      showModal(LAYOUT_MODAL_TYPES.clioExportWarning, {
        onConfirm: async () => {
          await onSaveInClio(true);
        },
        variant: newVersionAvailable
          ? MODAL_VARIANTS.WARNING
          : MODAL_VARIANTS.INFO,
        title: 'Save to Clio Manage',
        message:
          'Existing document(s)/document folder(s) will be replaced in Clio Manage and a new version will be created.',
        ...(newVersionAvailable && {
          alertMessage:
            'Any changes made to the existing documents in Clio Manage will be overridden.',
        }),
        ...(!newVersionAvailable && {
          showCheckbox: true,
          checkboxLabel: 'Do not show this message again',
        }),
      });
    } else {
      if (isDdhmEditEnabled && matterId && (!parentId || isFolderDeleted)) {
        // show folder picker modal for new document set creation
        showModal(LAYOUT_MODAL_TYPES.folderPicker, {
          matterId,
          onConfirm: async (selectedFolderId: number) => {
            await onSaveInClio(true, selectedFolderId);
          },
        });
      } else {
        onSaveInClio();
      }
    }
  };

  const renderTrigger = ({
    toggleDropdown,
  }: {
    toggleDropdown: () => void;
  }) => {
    toggleDropdownCallback = toggleDropdown;
    return (
      <span analyticsname="DownloadPdf">
        <Button disabled={disableDownload} onClick={toggleDropdown}>
          Download <Triangle primary orientation={'down'} />
        </Button>
      </span>
    );
  };

  const isEsignButtonDisabled = disableEsign || isProjectsFetching;

  return (
    <footer className={css(styles.documentPreviewFooter)}>
      <Button disabled={disableEdit} Icon={Edit3} onClick={onEdit}>
        Back
      </Button>
      <div className={css(styles.rightSideButtons)}>
        {clioStore.courtFormClioMatterId && (
          <span
            analyticsname={`Save to Clio Button Clicked - ${
              clioStore.courtFormClioParentId ? 'Edit' : 'New'
            }`}
          >
            <Button
              className={css(styles.clioButton)}
              disabled={disableSaveInClio}
              id="clioButton"
              onClick={handleSaveInClio}
            >
              Save to Clio Manage
            </Button>
          </span>
        )}
        {isHighlightingEnabled && (
          <div
            className={css(styles.documentPreviewHighlightToggle)}
            analyticsname="Highlight Changes"
          >
            <Checkbox
              checked={showHighlights}
              size={2}
              onClick={onToggleShowHighlights}
              disabled={false}
            />
            <span className={css(styles.documentPreviewHighlightToggleText)}>
              Highlight Changes
            </span>
          </div>
        )}
        <Dropdown
          renderTrigger={renderTrigger}
          className={css(styles.eSignButton)}
        >
          <a
            onClick={handleCurrentDownload}
            className={css(dropdownStyles.dropdownActionItem)}
            analyticsname="Download Current Document"
          >
            Download current document
          </a>
          {totalNumber > 1 && (
            <a
              onClick={handleAlDownload}
              className={css(dropdownStyles.dropdownActionItem)}
              analyticsname="Download All (.zip)"
            >
              Download all (.zip)
            </a>
          )}
        </Dropdown>
        {isEsignEnabled && (
          <Button
            primary
            disabled={isEsignButtonDisabled}
            onClick={onCreateEsign}
            id="esignButton"
          >
            Esign
          </Button>
        )}
      </div>
    </footer>
  );
};

DraftingFooter.defaultProps = {
  onCurrentDownload: () => {},
  onAllDownload: () => {},
  onEdit: () => {},
  onCreateEsign: () => {},
  onSaveInClio: () => {},
  onToggleShowHighlights: () => {},
  showHighlights: false,
  isHighlightingEnabled: false,
  newVersionAvailable: false,
};
